/**
 * Admin Feed Analytics Hooks
 *
 * Rankings de visualización del feed: top usuarios y top capturas.
 * RPCs: admin_get_feed_top_viewers, admin_get_feed_top_captures
 */

import { useQuery } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FeedTopViewer {
    user_id: string;
    display_name: string | null;
    avatar_url: string | null;
    total_impressions: number;
    total_view_duration_ms: number;
    avg_duration_ms: number | null;
}

export interface FeedTopCapture {
    capture_id: string;
    impressions_count: number;
    total_view_duration_ms: number;
    image: string | null;
    species: string | null;
    author_user_id: string;
    author_name: string | null;
    likes_count: number;
    comments_count: number;
}

export type FeedTopViewersOrderBy = 'total_impressions' | 'total_view_duration_ms' | 'avg_duration_ms';
export type FeedTopCapturesOrderBy = 'impressions' | 'time' | 'likes' | 'comments';
export type OrderDir = 'asc' | 'desc';

// ─── Hooks ──────────────────────────────────────────────────────────────────

export function useFeedTopViewers(
    days: number | null = 30,
    limit = 50,
    orderBy: FeedTopViewersOrderBy = 'total_view_duration_ms',
    orderDir: OrderDir = 'desc'
) {
    return useQuery<FeedTopViewer[]>({
        queryKey: ['admin', 'feed-top-viewers', days, limit, orderBy, orderDir],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_feed_top_viewers', {
                p_days: days,
                p_limit: limit,
                p_order_by: orderBy,
                p_order_dir: orderDir,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as FeedTopViewer[];
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}

export function useFeedTopCaptures(
    days: number | null = 30,
    limit = 50,
    orderBy: FeedTopCapturesOrderBy = 'impressions',
    orderDir: OrderDir = 'desc'
) {
    return useQuery<FeedTopCapture[]>({
        queryKey: ['admin', 'feed-top-captures', days, limit, orderBy, orderDir],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_feed_top_captures', {
                p_days: days,
                p_limit: limit,
                p_order_by: orderBy,
                p_order_dir: orderDir,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as FeedTopCapture[];
        },
        staleTime: 5 * 60_000,
        refetchOnWindowFocus: false,
    });
}
